import i18n, { type BackendModule, type Resource } from 'i18next'
import LanguageDetector from 'i18next-browser-languagedetector'
import { initReactI18next } from 'react-i18next'

import pl from './locales/pl/translation.json'
import en from './locales/en/translation.json'

type TranslationObject = Record<string, unknown>
type TranslationModule = { default: TranslationObject }

const PRIMARY_LANGUAGES = ['pl', 'en', 'de'] as const
const LOGIN_EXTRA_LANGUAGES = [
  'fr',
  'nl',
  'ga',
  'es',
  'pt',
  'it',
  'el',
  'mt',
  'tr',
  'da',
  'sv',
  'fi',
  'et',
  'lv',
  'lt',
  'cs',
  'sk',
  'hu',
  'sl',
  'hr',
  'ru',
  'uk',
  'be',
  'bg',
  'ro',
] as const

type PrimaryLanguage = (typeof PRIMARY_LANGUAGES)[number]
type LoginExtraLanguage = (typeof LOGIN_EXTRA_LANGUAGES)[number]
type SupportedLanguage = PrimaryLanguage | LoginExtraLanguage

export const SUPPORTED_LANGUAGES = Array.from(
  new Set([...PRIMARY_LANGUAGES, ...LOGIN_EXTRA_LANGUAGES])
) as SupportedLanguage[]

const isTranslationObject = (value: unknown): value is TranslationObject =>
  Boolean(value) && typeof value === 'object' && !Array.isArray(value)

const mergeWithFallback = (
  fallback: TranslationObject,
  translation?: TranslationObject | null
): TranslationObject => {
  if (!translation) {
    return { ...fallback }
  }

  const result: TranslationObject = { ...fallback }

  for (const [key, value] of Object.entries(translation)) {
    const fallbackValue = result[key]
    if (isTranslationObject(value) && isTranslationObject(fallbackValue)) {
      result[key] = mergeWithFallback(fallbackValue, value)
      continue
    }
    result[key] = value
  }

  return result
}

const englishBase = en as TranslationObject

const withEnglishFallback = (
  translation?: TranslationObject | null
): TranslationObject => mergeWithFallback(englishBase, translation)

export const normalizeSupportedLanguage = (language: string | null | undefined): SupportedLanguage => {
  const normalized = String(language ?? 'en').trim().toLowerCase().split('-')[0]
  return SUPPORTED_LANGUAGES.includes(normalized as SupportedLanguage)
    ? (normalized as SupportedLanguage)
    : 'en'
}

const dynamicTranslationLoaders: Partial<
  Record<Exclude<SupportedLanguage, 'pl' | 'en'>, () => Promise<TranslationModule>>
> = {
  de: () => import('./locales/de/translation.json'),
  fr: () => import('./locales/fr/translation.json'),
  nl: () => import('./locales/nl/translation.json'),
  ga: () => import('./locales/ga/translation.json'),
  es: () => import('./locales/es/translation.json'),
  pt: () => import('./locales/pt/translation.json'),
  it: () => import('./locales/it/translation.json'),
  el: () => import('./locales/el/translation.json'),
  mt: () => import('./locales/mt/translation.json'),
  tr: () => import('./locales/tr/translation.json'),
  da: () => import('./locales/da/translation.json'),
  sv: () => import('./locales/sv/translation.json'),
  fi: () => import('./locales/fi/translation.json'),
  et: () => import('./locales/et/translation.json'),
  lv: () => import('./locales/lv/translation.json'),
  lt: () => import('./locales/lt/translation.json'),
  cs: () => import('./locales/cs/translation.json'),
  sk: () => import('./locales/sk/translation.json'),
  hu: () => import('./locales/hu/translation.json'),
  sl: () => import('./locales/sl/translation.json'),
  hr: () => import('./locales/hr/translation.json'),
  ru: () => import('./locales/ru/translation.json'),
  uk: () => import('./locales/uk/translation.json'),
  be: () => import('./locales/be/translation.json'),
  bg: () => import('./locales/bg/translation.json'),
  ro: () => import('./locales/ro/translation.json'),
}

const bundledResources: Resource = {
  pl: { translation: withEnglishFallback(pl as TranslationObject) },
  en: { translation: englishBase },
}

const lazyTranslationBackend: BackendModule = {
  type: 'backend',
  init: () => undefined,
  read(language, _namespace, callback) {
    const normalized = normalizeSupportedLanguage(language)
    if (normalized === 'pl') {
      callback(null, bundledResources.pl?.translation ?? withEnglishFallback(pl as TranslationObject))
      return
    }

    if (normalized === 'en') {
      callback(null, bundledResources.en?.translation ?? englishBase)
      return
    }

    const loader = dynamicTranslationLoaders[normalized as Exclude<SupportedLanguage, 'pl' | 'en'>]
    if (!loader) {
      callback(null, bundledResources.en?.translation ?? englishBase)
      return
    }

    void loader()
      .then((module) => {
        callback(null, withEnglishFallback(module.default))
      })
      .catch((error: unknown) => {
        callback(error instanceof Error ? error : new Error('Unable to load language resources'), false)
      })
  },
}

// <html lang> za wybranym językiem (WCAG 3.1.1): czytniki ekranu i dzielenie wyrazów, index.html ma na sztywno „pl”
i18n.on('languageChanged', (lng) => {
  if (typeof document !== 'undefined' && lng) document.documentElement.lang = lng
})

void i18n
  .use(LanguageDetector)
  .use(lazyTranslationBackend)
  .use(initReactI18next)
  .init({
    resources: bundledResources,
    fallbackLng: 'en',
    supportedLngs: SUPPORTED_LANGUAGES,
    nonExplicitSupportedLngs: true,
    partialBundledLanguages: true,
    load: 'languageOnly',
    defaultNS: 'translation',
    ns: ['translation'],
    detection: {
      order: ['querystring', 'localStorage', 'navigator'],
      lookupQuerystring: 'lang',
      caches: ['localStorage'],
    },
    interpolation: {
      escapeValue: false,
    },
  })

export { PRIMARY_LANGUAGES }
export default i18n
